import type { CaseResultRecord } from '@/lib/blocks/collection/records';
import { readRecord, readString } from '@/lib/prop-utils';
import type { Court, PracticeArea } from '@/types/shared';
import { TEMPLATE_PREVIEW_PRACTICE_AREAS } from './preview-data';

function normalizeCourt(value: unknown): Court | null {
  const record = readRecord(value);
  if (!record) return null;

  const name = readString(record.name)?.trim();
  if (!name) return null;

  return {
    id: readString(record.id) ?? name,
    name,
  };
}

function normalizeCaseResultCourts(caseResult: CaseResultRecord) {
  if (Array.isArray(caseResult.courts)) return caseResult.courts;

  const court = normalizeCourt(readRecord(caseResult)?.court);
  return court ? [court] : undefined;
}

export function normalizeCaseResult(caseResult: CaseResultRecord): CaseResultRecord {
  return {
    ...caseResult,
    pageId: readString(caseResult.pageId),
    path: readString(caseResult.path),
    courts: normalizeCaseResultCourts(caseResult),
  };
}

function derivePracticeAreasFromCaseResults(caseResults: CaseResultRecord[]): PracticeArea[] {
  const practiceAreasByName = new Map<string, PracticeArea>();

  for (const caseResult of caseResults) {
    for (const practiceArea of caseResult.practiceAreas ?? []) {
      const name = readString(practiceArea.name)?.trim();
      if (!name || practiceAreasByName.has(name.toLowerCase())) continue;
      practiceAreasByName.set(name.toLowerCase(), practiceArea);
    }
  }

  return Array.from(practiceAreasByName.values());
}

export function resolveCaseResultFilterPracticeAreas({
  sectionPracticeAreas,
  caseResults,
  isTemplatePreview,
}: {
  sectionPracticeAreas?: PracticeArea[];
  caseResults: CaseResultRecord[];
  isTemplatePreview: boolean;
}): PracticeArea[] {
  if (sectionPracticeAreas?.length) return sectionPracticeAreas;

  const derivedPracticeAreas = derivePracticeAreasFromCaseResults(caseResults);
  if (derivedPracticeAreas.length > 0) return derivedPracticeAreas;

  return isTemplatePreview ? TEMPLATE_PREVIEW_PRACTICE_AREAS : [];
}
